

































import { Component, Prop, Vue } from 'vue-property-decorator';
import KpiDashboardItemDto from '@/models/KpiTargets/KpiDashboardItemDto';
import { getKpiLabel, getKpiUnit, formatKpiValue } from '@/models/KpiTargets/KpiCodeRegistry';

@Component({ name: 'KpiDashboardCard' })
export default class KpiDashboardCard extends Vue {
  @Prop({ required: true }) item!: KpiDashboardItemDto;

  get kpiLabel(): string {
    const target = this.item ? this.item.target : null;
    return target ? getKpiLabel(target.kpiCode) : '';
  }

  get periodLabel(): string {
    return this.item ? this.item.periodLabel : '';
  }

  get scopeLabel(): string {
    const target = this.item ? this.item.target : null;
    if (!target) return '';
    if (target.agencyUser && target.agencyUser.person) {
      return target.agencyUser.person.firstName + ' ' + target.agencyUser.person.lastName;
    }
    if (target.desk) {
      return target.desk.name;
    }
    if (target.branch) {
      return target.branch.brandName;
    }
    return 'Agency';
  }

  get targetUnit(): string {
    const target = this.item ? this.item.target : null;
    return target ? getKpiUnit(target.kpiCode) : 'count';
  }

  get targetValue(): number {
    const target = this.item ? this.item.target : null;
    return target ? target.targetValue : 0;
  }

  get dimensionChips(): string[] {
    const target = this.item ? this.item.target : null;
    if (!target) return [];
    const chips: string[] = [];

    if (target.positions && target.positions.length > 0) {
      chips.push('Positions: ' + target.positions.map((p: any) => p.name).join(', '));
    }
    if (target.activityCategories && target.activityCategories.length > 0) {
      chips.push('Categories: ' + target.activityCategories.map((c: any) => c.name).join(', '));
    }
    if (target.locationTypes && target.locationTypes.length > 0) {
      chips.push('Location Types: ' + target.locationTypes.map((lt: any) => lt.name).join(', '));
    }
    if (target.skills && target.skills.length > 0) {
      chips.push('Skills: ' + target.skills.map((s: any) => s.name).join(', '));
    }
    if (target.qualifications && target.qualifications.length > 0) {
      chips.push('Qualifications: ' + target.qualifications.map((q: any) => q.name).join(', '));
    }

    return chips;
  }

  get formattedActual(): string {
    return formatKpiValue(this.item ? this.item.actual : 0, this.targetUnit);
  }

  get formattedTarget(): string {
    return formatKpiValue(this.targetValue, this.targetUnit);
  }

  get progressPercent(): number {
    if (this.targetValue <= 0) return 0;
    return Math.min(100, Math.round((this.item.actual / this.targetValue) * 100));
  }

  get isProRated(): boolean {
    return this.item && this.item.proRatedTarget > 0 && this.item.proRatedTarget < this.targetValue;
  }

  get showPaceIndicator(): boolean {
    return this.item && this.item.supported && this.isProRated;
  }

  get formattedProRatedTarget(): string {
    return formatKpiValue(this.item ? this.item.proRatedTarget : 0, this.targetUnit);
  }

  get paceStatus(): string {
    if (!this.item) return 'behind';
    const actual = this.item.actual;
    const expected = this.item.proRatedTarget;
    if (actual >= expected) return 'on_track';
    if (actual >= expected * 0.8) return 'slightly_behind';
    return 'behind';
  }

  get paceStatusLabel(): string {
    switch (this.paceStatus) {
      case 'on_track': return 'On track';
      case 'slightly_behind': return 'Slightly behind';
      default: return 'Behind pace';
    }
  }

  get paceTextClass(): string {
    switch (this.paceStatus) {
      case 'on_track': return 'success--text';
      case 'slightly_behind': return 'amber--text text--darken-2';
      default: return 'error--text';
    }
  }

  get statusColor(): string {
    if (this.isProRated) {
      // Use pro-rated comparison for in-progress periods
      if (this.paceStatus === 'on_track') return 'success';
      if (this.paceStatus === 'slightly_behind') return 'amber';
      return 'error';
    }
    // Completed period — compare against full target
    if (this.progressPercent >= 100) return 'success';
    if (this.progressPercent >= 75) return 'amber';
    return 'error';
  }

  get statusTextClass(): string {
    if (this.isProRated) {
      return this.paceTextClass;
    }
    if (this.progressPercent >= 100) return 'success--text';
    if (this.progressPercent >= 75) return 'amber--text text--darken-2';
    return 'error--text';
  }
}
